import { useEffect, useState } from 'react';
import { getTournamentCancelPartnerInvitationApi } from 'constants/payloads/tournamentCancelPartnerInvitation';
import { getTournamentInviteByEmailApi } from 'constants/payloads/tournamentInviteByEmail';
import {
  getTournamentPartnerRequestAcceptApi,
  getTournamentPartnerRequestApi,
  getTournamentPartnerRequestDenyApi,
} from 'constants/payloads/tournamentPartnerRequest';
import { VairUser } from 'constants/payloads/vair/playerSearch';
import {
  EventGroupRegistrationStatusesEnum,
  EventInvitationStatusesEnum,
  EventInvitationTypesEnum,
  EventTeamMemberStatusesEnum,
  GetUserEventRegistrationQuery,
  TeamTypesEnum,
} from 'types/generated/client';
import { formatVairRating, parseVairSportRating } from 'utils/shared/vair/parsePrimaryRating';
import { useApiGateway } from 'hooks/useApi';
import { useModal } from 'hooks/useModal';
import CloseIcon from 'svg/CloseIcon';
import Share from 'svg/Share';
import Button, { ButtonText } from 'components/Button';
import RegistrationStatusBadge, {
  RegistrationStatusEnum,
} from 'components/RegistrationStatusBadge';
import Modal from 'components/modals/Modal';
import PartnerActionSection from './PartnerActionSection';
import ReceivedPartnerRequestCard from './ReceivedPartnerRequestCard';
import type { UserNeedingPartner } from './utils';
import { buildShareUrl } from './utils';
import ModalInvitePartner from '../ModalInvitePartner';
import ModalSearchPartner from '../ModalSearchPartner';

export type EventGroupRegistration =
  GetUserEventRegistrationQuery['eventGroupRegistrations'][number];

export type PartnerActionMode = 'idle' | 'requesting' | 'adding-by-email';

export type ReceivedPartnerRequest = {
  id: string;
  groupId: string;
  groupRegistrationId?: string | null;
  senderUserId: string | null;
  senderUserProfile?: { fullName?: string | null; preferredName?: string | null } | null;
};

export interface EventGroupRegistrationCardProps {
  registration: EventGroupRegistration;
  eventTitle: string;
  eventId: string;
  index: number;
  userName: string;
  userId: string | null | undefined;
  usersNeedingPartner: UserNeedingPartner[];
  receivedPartnerRequests?: ReceivedPartnerRequest[];
  onShareDataChange: (data: { title: string; url: string } | null) => void;
  onPartnerActionModeChange?: (registrationId: string, mode: PartnerActionMode) => void;
  onPartnerRequestSent?: () => void;
  vairUsersMap?: Record<string, VairUser>;
  hasPartners?: boolean;
}

const memberStatusRowClassName = 'mt-6 flex items-center justify-between';
const memberNameClassName = 'typography-product-body-highlight text-ellipsis';

export default function EventGroupRegistrationCard({
  registration,
  eventTitle,
  eventId,
  index,
  userName,
  userId,
  usersNeedingPartner,
  receivedPartnerRequests = [],
  onShareDataChange,
  onPartnerActionModeChange,
  onPartnerRequestSent,
  vairUsersMap,
  hasPartners = true,
}: EventGroupRegistrationCardProps) {
  const { group } = registration;
  const teamMembers = group.teams.flatMap((team) => team.members);
  const otherTeamMembers = teamMembers.filter((t) => t.userId !== userId);
  const pendingInvitations = registration.invitations.filter(
    (invite) => invite.status !== EventInvitationStatusesEnum.Accepted,
  );

  const isDoublesRequiringPartner =
    hasPartners && group.teamType === TeamTypesEnum.Doubles && otherTeamMembers.length === 0;

  const [partnerActionMode, setPartnerActionMode] = useState<PartnerActionMode>('idle');
  const [partnerEmail, setPartnerEmail] = useState('');
  const [isChangingPartner, setIsChangingPartner] = useState(false);
  const {
    isOpen: isConfirmChangeOpen,
    openModal: openConfirmChange,
    closeModal: closeConfirmChange,
  } = useModal();

  const handlePartnerModeChange = (mode: PartnerActionMode) => {
    setPartnerActionMode(mode);
    if (mode === 'idle' && isChangingPartner) {
      setIsChangingPartner(false);
    }
  };

  const { post: cancelPartnerInvitation, isLoading: isCancellingInvitation } = useApiGateway(
    getTournamentCancelPartnerInvitationApi(eventId),
  );
  const { post: sendPartnerRequest, isLoading: isSendingPartnerRequest } = useApiGateway(
    getTournamentPartnerRequestApi(eventId),
  );
  const { post: sendEmailInvite, isLoading: isSendingEmailInvite } = useApiGateway(
    getTournamentInviteByEmailApi(eventId),
  );

  const handleConfirmChange = async () => {
    await cancelPartnerInvitation({ payload: { groupRegistrationId: registration.id } });
    setIsChangingPartner(true);
    closeConfirmChange();
    onPartnerRequestSent?.();
  };
  const { post: acceptPartnerRequest, isLoading: isAccepting } = useApiGateway(
    getTournamentPartnerRequestAcceptApi(eventId),
  );
  const { post: denyPartnerRequest, isLoading: isDenying } = useApiGateway(
    getTournamentPartnerRequestDenyApi(eventId),
  );
  const isRespondingToRequest = isAccepting || isDenying;

  const handleAcceptRequest = async (invitationId: string) => {
    await acceptPartnerRequest({ payload: { invitationId } });
    onPartnerRequestSent?.();
  };

  const handleDenyRequest = async (invitationId: string) => {
    await denyPartnerRequest({ payload: { invitationId } });
    onPartnerRequestSent?.();
  };

  useEffect(() => {
    onPartnerActionModeChange?.(registration.id, partnerActionMode);
  }, [registration.id, partnerActionMode, onPartnerActionModeChange]);

  const handleInviteClick = () => {
    const teamId = group.teams.find((team) => team.members.length >= 1)?.id;
    const invitationId = registration.invitations.find(
      (invite) => invite.status !== EventInvitationStatusesEnum.Accepted,
    )?.id;
    const shareUrl = buildShareUrl(eventId, teamId, invitationId);

    onShareDataChange({
      title: `Share ${group.title || 'Event'}`,
      url: shareUrl,
    });

    if (navigator.share) {
      const shareText = `Pickleball tournament on Bounce${group.title ? ` - ${group.title}` : ''}${
        eventTitle ? ` - ${eventTitle}` : ''
      }. Join me!`;
      navigator
        .share({ title: shareText, text: shareText, url: shareUrl })
        .then(() => console.log('Successful share'))
        .catch((error) => console.log('Error sharing:', error));
    }
  };

  const handleRequestUser = async (user: UserNeedingPartner) => {
    if (!user.userId || !registration.id) return;
    await sendPartnerRequest({
      payload: {
        groupRegistrationId: registration.id,
        groupId: group.id,
        invitedUserId: user.userId!,
      },
    });
    setPartnerActionMode('idle');
    setIsChangingPartner(false);
    onPartnerRequestSent?.();
  };

  // "Add partner" modal — opens ModalSearchPartner for immediate partner actions
  const {
    isOpen: isSearchPartnerOpen,
    openModal: openSearchPartner,
    closeModal: closeSearchPartner,
  } = useModal();
  const {
    isOpen: isInvitePartnerOpen,
    openModal: openInvitePartner,
    closeModal: closeInvitePartner,
  } = useModal();

  const handleSearchPartnerSelect = async (user: UserNeedingPartner) => {
    if (!user.userId || !registration.id) return;
    await sendPartnerRequest({
      payload: {
        groupRegistrationId: registration.id,
        groupId: group.id,
        invitedUserId: user.userId,
      },
    });
    closeSearchPartner();
    setPartnerActionMode('idle');
    setIsChangingPartner(false);
    onPartnerRequestSent?.();
  };

  const handleSearchPartnerInviteDb = async (user: {
    id?: string | null;
    fullName?: string | null;
    username?: string | null;
  }) => {
    if (!user.id || !registration.id) return;
    await sendPartnerRequest({
      payload: {
        groupRegistrationId: registration.id,
        groupId: group.id,
        invitedUserId: user.id,
      },
    });
    closeSearchPartner();
    setPartnerActionMode('idle');
    setIsChangingPartner(false);
    onPartnerRequestSent?.();
  };

  const handleSearchPartnerInviteEmail = async (email: string) => {
    const teamId = group.teams.find((team) => team.members.length >= 1)?.id;
    if (!teamId || !registration.id) return;
    await sendEmailInvite({
      payload: {
        groupRegistrationId: registration.id,
        groupId: group.id,
        teamId,
        partnerEmail: email,
      },
    });
    closeInvitePartner();
    setPartnerActionMode('idle');
    setIsChangingPartner(false);
    onPartnerRequestSent?.();
  };

  const currentUserParsed = parseVairSportRating(
    userId ? vairUsersMap?.[userId]?.sport?.pickleball : undefined,
  );
  const currentUserRatingDisplay = currentUserParsed.formattedValue;

  const getInvitationStatus = (invite: EventGroupRegistration['invitations'][number]) => {
    if (invite.status === EventInvitationStatusesEnum.Accepted)
      return RegistrationStatusEnum.Registered;
    if (invite.invitationType === EventInvitationTypesEnum.PartnerRequest)
      return RegistrationStatusEnum.Requested;
    return RegistrationStatusEnum.Pending;
  };

  const getInvitationDisplayName = (invite: EventGroupRegistration['invitations'][number]) => {
    const profile = invite.invitedUserProfile;
    if (profile) return profile.fullName || profile.preferredName || 'Partner';
    return invite.invitationEmail || 'Partner';
  };

  return (
    <div className="mt-8 border-t border-color-border-input-lightmode pt-8 dark:border-color-border-input-darkmode">
      <div className="flex w-full items-center justify-between">
        <div className="min-w-0 flex-1">
          <h3 className="typography-product-subheading truncate">
            {group.title || `Event #${index + 1}`}
          </h3>
        </div>
        {otherTeamMembers.length === 0 && !isDoublesRequiringPartner && (
          <ButtonText
            className="typography-product-subheading ml-2 flex shrink-0 items-center whitespace-nowrap pr-2 text-color-brand-primary"
            onClick={handleInviteClick}
          >
            <Share className="mr-1 h-4 w-4" /> Invite
          </ButtonText>
        )}
      </div>
      <div className="space-y-4">
        <div className={memberStatusRowClassName}>
          <div className={memberNameClassName}>
            {userName || 'You'}
            {currentUserRatingDisplay && (
              <span className="typography-product-body ml-1 text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                (VAIR {currentUserRatingDisplay})
              </span>
            )}
          </div>
          <RegistrationStatusBadge
            status={
              registration.status === EventGroupRegistrationStatusesEnum.Active
                ? RegistrationStatusEnum.Registered
                : RegistrationStatusEnum.Pending
            }
          />
        </div>
        {otherTeamMembers.map((member) => {
          const parsed = parseVairSportRating(
            member.userId ? vairUsersMap?.[member.userId]?.sport?.pickleball : undefined,
          );
          const ratingDisplay = parsed.formattedValue;
          return (
            <div key={member.id} className={memberStatusRowClassName}>
              <div className={memberNameClassName}>
                {member.userProfile?.fullName || 'Partner'}
                {ratingDisplay && (
                  <span className="typography-product-body ml-1 text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                    (VAIR {ratingDisplay})
                  </span>
                )}
              </div>
              <RegistrationStatusBadge
                status={
                  member.status === EventTeamMemberStatusesEnum.Active
                    ? RegistrationStatusEnum.Registered
                    : RegistrationStatusEnum.Pending
                }
              />
            </div>
          );
        })}
        {!isChangingPartner &&
          otherTeamMembers.length === 0 &&
          pendingInvitations.map((invite) => {
            const inviteUserId = invite.invitedUserProfile?.id;
            const parsed = parseVairSportRating(
              inviteUserId ? vairUsersMap?.[inviteUserId]?.sport?.pickleball : undefined,
            );
            const ratingDisplay = parsed.formattedValue;
            return (
              <div key={invite.id} className={memberStatusRowClassName}>
                <div className={memberNameClassName}>
                  {getInvitationDisplayName(invite)}
                  {ratingDisplay && (
                    <span className="typography-product-body ml-1 text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                      (VAIR {ratingDisplay})
                    </span>
                  )}
                </div>
                <RegistrationStatusBadge status={getInvitationStatus(invite)} />
              </div>
            );
          })}
        {!isChangingPartner && isDoublesRequiringPartner && pendingInvitations.length > 0 && (
          <Button variant="secondary" type="button" onClick={() => openConfirmChange()} size="md">
            Change partner
          </Button>
        )}
        {isDoublesRequiringPartner &&
          !isChangingPartner &&
          receivedPartnerRequests
            .filter((r) => r.groupId === group.id)
            .map((request) => (
              <ReceivedPartnerRequestCard
                key={request.id}
                request={request}
                isLoading={isRespondingToRequest}
                onAccept={handleAcceptRequest}
                onDeny={handleDenyRequest}
                rating={
                  request.senderUserId
                    ? formatVairRating(vairUsersMap?.[request.senderUserId]?.sport?.pickleball)
                    : undefined
                }
              />
            ))}
        {isDoublesRequiringPartner &&
          (isChangingPartner ||
            (pendingInvitations.length === 0 &&
              receivedPartnerRequests.filter((r) => r.groupId === group.id).length === 0)) && (
            <PartnerActionSection
              mode={partnerActionMode}
              partnerEmail={partnerEmail}
              usersNeedingPartner={usersNeedingPartner}
              isLoading={isSendingPartnerRequest || isSendingEmailInvite}
              onModeChange={handlePartnerModeChange}
              onPartnerEmailChange={setPartnerEmail}
              vairUsersMap={vairUsersMap}
              onSendRequest={async () => {
                const teamId = group.teams.find((team) => team.members.length >= 1)?.id;
                if (!teamId) return;
                await sendEmailInvite({
                  payload: {
                    groupRegistrationId: registration.id,
                    groupId: group.id,
                    teamId,
                    partnerEmail,
                  },
                });
                setPartnerActionMode('idle');
                setPartnerEmail('');
                setIsChangingPartner(false);
                onPartnerRequestSent?.();
              }}
              onRequestUser={handleRequestUser}
              onAddPartner={openSearchPartner}
            />
          )}
      </div>
      <ModalSearchPartner
        isOpen={isSearchPartnerOpen}
        onClose={closeSearchPartner}
        usersNeedingPartner={usersNeedingPartner}
        vairUsersMap={vairUsersMap}
        onSelectPartner={handleSearchPartnerSelect}
        onInvitePartner={() => {
          closeSearchPartner();
          openInvitePartner();
        }}
        onInviteDatabaseUser={handleSearchPartnerInviteDb}
        isLoading={isSendingPartnerRequest}
      />
      <ModalInvitePartner
        isOpen={isInvitePartnerOpen}
        onClose={closeInvitePartner}
        onInvite={handleSearchPartnerInviteEmail}
      />
      <Modal isOpen={isConfirmChangeOpen} handleClose={closeConfirmChange}>
        <div className="flex flex-col p-ds-3xl pb-ds-xl">
          <div className="flex flex-col items-center justify-center px-ds-3xl text-center">
            <img src="/images/players/alert.png" className="h-auto w-20" />
            <h3 className="typography-product-display mt-6 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              Change partner
            </h3>
            <p className="typography-product-body mt-ds-md text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              Changing your partner will cancel your current partner request.
            </p>
          </div>
          <div className="mt-12  flex w-full flex-col gap-ds-lg">
            <Button
              variant="inverted"
              className="w-full"
              size="lg"
              onClick={() => closeConfirmChange()}
            >
              Go Back
            </Button>
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={handleConfirmChange}
              loading={isCancellingInvitation}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
